import { useTranslation } from "react-i18next";

/**
 * Shared chrome for connector preview cards. Security posture (binding):
 * every value here comes from a third-party MCP server and is rendered as
 * plain text nodes only — no HTML, no markdown. Display strings are clamped
 * and lists are capped so a hostile payload can't blow up the thread.
 */

/** Preview cards never render more than this many rows. */
export const MAX_PREVIEW_ROWS = 8;

/** Clamp an attacker-influenceable display string to a sane length. */
export function clampText(value: string, max = 300): string {
  return value.length > max ? `${value.slice(0, max)}…` : value;
}

/** Localize a tool-provided date string, falling back to the raw (clamped) text. */
export function formatPreviewDate(value: string, locale: string): string {
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return clampText(value, 40);
  const sameYear = date.getFullYear() === new Date().getFullYear();
  return date.toLocaleDateString(
    locale,
    sameYear ? { month: "short", day: "numeric" } : { year: "numeric", month: "short", day: "numeric" },
  );
}

/** Empty-list state inside a preview card. */
export function PreviewEmpty() {
  const { t } = useTranslation();
  return (
    <p className="px-3 py-2.5 font-sans text-[12.5px] text-ink-muted">{t("chat.preview.empty")}</p>
  );
}

/** Truncation footer when a card caps its list at MAX_PREVIEW_ROWS. */
export function PreviewShowingOf({ shown, total }: { shown: number; total: number }) {
  const { t } = useTranslation();
  return (
    <p className="px-3 py-1.5 font-sans text-[11.5px] text-ink-muted">
      {t("chat.preview.showingOf", { shown, total })}
    </p>
  );
}

/**
 * The card footer's outbound link. `href` MUST already be https-vetted by the
 * caller (`safeHttpsUrl` or a hardcoded https constant) — this component
 * deliberately takes a plain string so cards keep the vetting visible at the
 * call site.
 */
export function PreviewFooterLink({ href, connector }: { href: string; connector: string }) {
  const { t } = useTranslation();
  return (
    <div className="px-3 pb-2.5 pt-1">
      <a
        href={href}
        target="_blank"
        rel="noreferrer noopener"
        className="font-sans text-[12.5px] font-medium text-ink underline underline-offset-2 transition-colors duration-150 ease-house hover:text-ink"
      >
        {t("chat.mcpTool.openIn", { connector })}
      </a>
    </div>
  );
}
